import { useEffect, useState } from "react";
import algorithmic from "./tables/algorithmic.json";
import jamo from "./tables/jamo.json";

// The names `bun run unicode` writes, named through a glob rather than one import each so that a plane filled in
// upstream needs no edit here. `?url` is what keeps 1.4 MB out of this chunk: each file is 4096 code points' worth
// of names, emitted as an asset of its own and read only when a text has a character in that part of the standard.
const FILES = { "./names/000.json": __glob_0, "./names/001.json": __glob_1, "./names/002.json": __glob_2, "./names/003.json": __glob_3, "./names/004.json": __glob_4, "./names/00A.json": __glob_5, "./names/00D.json": __glob_6, "./names/00F.json": __glob_7, "./names/010.json": __glob_8, "./names/011.json": __glob_9, "./names/012.json": __glob_10, "./names/013.json": __glob_11, "./names/014.json": __glob_12, "./names/016.json": __glob_13, "./names/018.json": __glob_14, "./names/01A.json": __glob_15, "./names/01B.json": __glob_16, "./names/01C.json": __glob_17, "./names/01D.json": __glob_18, "./names/01E.json": __glob_19, "./names/01F.json": __glob_20, "./names/02F.json": __glob_21, "./names/0E0.json": __glob_22 } as Record<
  string,
  string
>;

// What a page renders with while the file a character's name is in is on its way, and what tells it to render again.
export interface Names {
  nameOf: (code: number) => string;
  reading: boolean;
}

export function useNames(codes: number[]): Names {
  const files = [...new Set(codes.map(fileOf))].filter((file) => file in FILES).sort();
  // The list as one string, so the effect runs when the set of files changes and not when the text does.
  const wanted = files.join(" ");
  const [, arrived] = useState(0);

  useEffect(() => {
    const asked = wanted === "" ? [] : wanted.split(" ");
    if (asked.every((file) => loaded.has(file))) return;

    let waiting = true;
    // Redrawn once for the lot rather than once each: a text in four scripts is four files and one more render.
    Promise.all(asked.map(read)).then(() => waiting && arrived((drawn) => drawn + 1));
    return () => {
      waiting = false;
    };
  }, [wanted]);

  // Answered either way rather than loaded, or a file the network dropped would leave the spinner turning for good.
  return { nameOf, reading: files.some((file) => !settled.has(file)) };
}

// The name of a code point, or nothing at all while the file holding it is still being read — which `FactTable` and
// the character table both draw as the row simply not being there yet, rather than as a name that is missing.
export function nameOf(code: number): string {
  const computed = computedName(code);
  if (computed !== "") return computed;
  return loaded.get(fileOf(code))?.[offsetOf(code)] ?? "";
}

// A hundred thousand ideographs and eleven thousand Hangul syllables are names nobody wrote down: an ideograph is
// its prefix and its code point, and a syllable is the three jamo it is composed of, spelled out in order.
function computedName(code: number): string {
  for (const range of algorithmic) {
    if (code < range.start || code > range.end) continue;
    if (range.kind !== "hangul") return range.prefix + code.toString(16).toUpperCase().padStart(4, "0");
    const index = code - range.start;
    const trailing = index % jamo.trailing.length;
    const vowel = Math.floor(index / jamo.trailing.length) % jamo.vowel.length;
    const leading = Math.floor(index / (jamo.trailing.length * jamo.vowel.length));
    return range.prefix + jamo.leading[leading] + jamo.vowel[vowel] + jamo.trailing[trailing];
  }
  return "";
}

const loaded = new Map<string, Record<string, string>>();
const asking = new Map<string, Promise<void>>();
// Every file that has been answered, whether with names or with nothing, which is what the spinner is drawn from.
const settled = new Set<string>();

function read(file: string): Promise<void> {
  const held = asking.get(file);
  if (held) return held;

  const asked = fetch(FILES[file])
    .then((response) => response.ok ? response.json() as Promise<Record<string, string>> : undefined)
    .catch(() => undefined)
    .then((names) => {
      settled.add(file);
      // A file the network dropped is worth asking for again, so only an answer is kept and the asking is dropped:
      // the next text reaching into this part of the standard asks afresh, and the names are absent until one does.
      if (names) loaded.set(file, names);
      else asking.delete(file);
    });

  asking.set(file, asked);
  return asked;
}

// The file a code point's name is in and the key it is under: the top bits name the file and the low twelve are the
// key, so a name costs one fetch of the part of the standard it belongs to and never one of the whole of it.
function fileOf(code: number): string {
  return `./names/${(code >> 12).toString(16).toUpperCase().padStart(3, "0")}.json`;
}

function offsetOf(code: number): string {
  return (code & 0xFFF).toString(16).toUpperCase().padStart(3, "0");
}

import __glob_0 from "./names/000.json?url";
import __glob_1 from "./names/001.json?url";
import __glob_2 from "./names/002.json?url";
import __glob_3 from "./names/003.json?url";
import __glob_4 from "./names/004.json?url";
import __glob_5 from "./names/00A.json?url";
import __glob_6 from "./names/00D.json?url";
import __glob_7 from "./names/00F.json?url";
import __glob_8 from "./names/010.json?url";
import __glob_9 from "./names/011.json?url";
import __glob_10 from "./names/012.json?url";
import __glob_11 from "./names/013.json?url";
import __glob_12 from "./names/014.json?url";
import __glob_13 from "./names/016.json?url";
import __glob_14 from "./names/018.json?url";
import __glob_15 from "./names/01A.json?url";
import __glob_16 from "./names/01B.json?url";
import __glob_17 from "./names/01C.json?url";
import __glob_18 from "./names/01D.json?url";
import __glob_19 from "./names/01E.json?url";
import __glob_20 from "./names/01F.json?url";
import __glob_21 from "./names/02F.json?url";
import __glob_22 from "./names/0E0.json?url";
